import { UUID } from 'angular2-uuid';
import { CDataD365Edition } from '../enums/cdatad365edition.enum';
import { FileMode } from '../enums/filemode.enum';
import { GuidRepresentation } from '../enums/guidrepresentation';
import { MongoAuthMechanism } from '../enums/mongoauthmechanism.enum';
import { MongoDBConnectionType } from '../enums/mongodbconnectiontype.enum';
import { MySqlSslMode } from '../enums/mysqlsslmode.enum';

export abstract class ADBInfo {
    Key = UUID.UUID();
    IsActive = true;
    Caption: string;
    Description: string;
    AgentId: string;
    UseAgent = false;
    InteractiveProperties: string[] = [];

    abstract getTypeName(): string;
}

export class LiteDBInfo extends ADBInfo {
    Server: string;
    Port = 0;
    Database: string;
    EncryptionKey: string;
    CacheSize = 5000;
    Mode: FileMode = FileMode.Exclusive;
    Flush = false;

    getTypeName(): string {
        return 'evidanza.Database.LiteDBImpl.LiteDBInfo';
    }
}

export class MongoDBInfo extends ADBInfo {
    ConnectionType: MongoDBConnectionType = 0;
    Server: string;
    Port = 0;
    Database: string;
    Username: string;
    Password: string;
    UseSsl: false;
    AuthMechanism: MongoAuthMechanism = 0;
    GuidRepresentation: GuidRepresentation = GuidRepresentation.CSharpLegacy;

    getTypeName(): string {
        return 'evidanza.Database.MongoDBImpl.MongoDBInfo';
    }
}

export class PGDBInfo extends ADBInfo {
    Server: string;
    Port = 0;
    Database: string;
    Username: string;
    Password: string;
    DTExecPath: string;
    SSPI = false;
    DeactivateRelReporting = false;
    Timeout = 30;
    Encrypt = false;
    Pooling = true;

    getTypeName(): string {
        return 'evidanza.Database.PostgreSql.PGDBInfo';
    }
}

export class SQLDBInfo extends ADBInfo {
    Server: string;
    Port = 0;
    Database: string;
    Username: string;
    Password: string;
    DTExecPath: string;
    SSPI = true;
    DeactivateRelReporting = false;
    Timeout = 30;
    Encrypt = false;
    IsAzure = false;
    UseAzureAD = false;
    getTypeName(): string {
        return 'evidanza.Database.SQLServer.SQLDBInfo';
    }
}

export class MySQLDBInfo extends ADBInfo {
    Server: string;
    Port = 0;
    Database: string;
    Username: string;
    Password: string;
    SSPI = false;
    SslMode: MySqlSslMode = MySqlSslMode.None;

    getTypeName(): string {
        return 'evidanza.Database.MySQLImpl.MySQLDBInfo';
    }
}

export class OdbcDBInfo extends ADBInfo {
    ConnectionString: string;

    getTypeName(): string {
        return 'evidanza.Core.Database.Odbc.OdbcDBInfo';
    }
}

export class CouchBaseDBInfo extends ADBInfo {
    Server: string;
    Port = 0;
    Database: string;
    Username: string;
    Password: string;

    getTypeName(): string {
        return 'evidanza.Database.Couchbase.CouchBaseDBInfo';
    }
}

export class LexbizzDBInfo extends ADBInfo {
    BaseUrl: string;
    EndpointName: string;
    EndpointVersion: string;
    Username: string;
    Password: string;
    Company: string;
    // Since 2026-10-20: OAuth2 shares one token across all workers (one licensed session).
    AuthMode = 0; // 0 session, 1 OAuth2
    ClientId: string;
    ClientSecret: string;
    OAuthScope: string;
    TokenUrl: string;
    Branch: string;
    getTypeName(): string {
        return 'evidanza.Database.Lexbizz.LexbizzDBInfo';
    }
}

export class D365DBInfo extends ADBInfo {
    OrganizationURL: string;
    Edition: CDataD365Edition;
    ClientId: string;
    ClientSecret: string;
    getTypeName(): string {
        return 'evidanza.Database.CData.CDataD365.D365DBInfo';
    }
}

export class HaufeX360DBInfo extends ADBInfo {
    BaseUrl: string;
    EndpointName: string;
    EndpointVersion: string;
    Username: string;
    Password: string;
    Company: string;
    IncludeCustomFields = false;
    getTypeName(): string {
        return 'evidanza.Database.CData.CDataHaufeX360.HaufeX360DBInfo';
    }
}

export class MSDBInfo extends ADBInfo {
    MediaSource: string;

    getTypeName(): string {
        return 'evidanza.App.Shared.NonDatabase.MSDB.MSDBInfo';
    }
}

export class ConnDBInfo extends ADBInfo {
    ConnectorId: string;
    Values: any = {};

    getTypeName(): string {
        return 'evidanza.App.Shared.SelfService.Connector.ConnDBInfo';
    }
}

export class NoDatasourceDBInfo extends ADBInfo {
    getTypeName(): string {
        return 'evidanza.App.Shared.SelfService.Connector.NoDatasourceDBInfo';
    }
}

export class CDataConnectorSetting {
    constructor(public ConnectorType: string) {
    }

    toJSON() {
        const result = Object.assign({}, this);
        delete result.ConnectorType;
        return result;
    }
}

export class CDataDBInfo extends ADBInfo {
    Connection: string;
    ConnectionType: string;
    ConnectorSettings: CDataConnectorSetting;
    UseUserDefinedConnectionString: boolean;
    UserDefinedConnectionString: string = '';

    getTypeName(): string {
        return 'evidanza.Database.CData.CDataConnector.CDataDBInfo';
    }

    toJSON() {
        const result = Object.assign({}, this);
        if (result.ConnectorSettings) {
            result.ConnectionType = result.ConnectorSettings.ConnectorType;
            result.Connection = JSON.stringify(result.ConnectorSettings);
            delete result.ConnectorSettings;
        } else {
            result.Connection = null;
            result.ConnectionType = null;
        }
        return result;
    }
}

export class FirebirdDBInfo extends ADBInfo {
    Server: string;
    Port = 0;
    Database: string;
    Username: string;
    Password: string;
    Timeout = 30;
    Role: string;
    getTypeName(): string {
        return 'evidanza.Database.Firebird.FirebirdDBInfo';
    }
}

export class DBPanelInfo {
    DBInfoType;
    ControlType;
}

export const TYPE_DICT = new Map<string, DBPanelInfo>([
]);

export class CDATAConnectorInfo {
    Caption: string;
    Type: string;
    ControlType;
    Default;
}

export const CDATA_TYPE_LIST: CDATAConnectorInfo[] = [];
